import type {
	SourcingLineStatusValue,
	SourcingRequestStatusValue,
} from '@supply-mover-2-0/shared';
import {
	SourcingLineStatus,
	SourcingRequestStatus,
} from '@supply-mover-2-0/shared';

/**
 * Inputs needed to derive an SR's master status from its line states.
 *
 * `currentStatus` is consulted first so two terminal/sticky states are
 * preserved without round-tripping through derivation:
 *   - `Cancelled` is a one-way manual override; it stays cancelled until the
 *     SR is explicitly re-opened by setting the status to something else.
 *   - `Draft` is sticky until the user explicitly flips out of it (via the
 *     "Save & Open" button on the create/edit sidebar). Adding lines while
 *     in Draft does NOT auto-flip to Open — purchasing shouldn't see WIP.
 */
export interface DeriveSourcingRequestStatusInput {
	currentStatus:
		| SourcingRequestStatusValue
		| SourcingRequestStatus
		| string
		| null
		| undefined;
	lineStatuses: ReadonlyArray<
		SourcingLineStatusValue | SourcingLineStatus | string
	>;
}

const ACTIVE_PO_LINE_STATUSES: ReadonlySet<string> = new Set([
	SourcingLineStatus.PO_PLACED,
	SourcingLineStatus.PARTIALLY_RECEIVED,
	SourcingLineStatus.RECEIVED,
]);

/**
 * Compute the SR master status from its line statuses + current status.
 *
 * Rule order (top-down, first match wins):
 *   1. currentStatus === Cancelled                                  → Cancelled (sticky manual override)
 *   2. currentStatus === Draft                                      → Draft (sticky until explicit user flip)
 *   3. Lines exist AND every line is Cancelled                      → Cancelled
 *   4. No lines (post-Draft back-stop)                              → Open
 *   5. Every non-cancelled line is Fulfilled                        → Fulfilled
 *   6. Some non-cancelled lines are Fulfilled (but not all)         → PartiallyFulfilled
 *   7. Any non-cancelled line is at/past PO_PLACED                  → InProgress
 *   8. All non-cancelled lines are Pending/VendorIdentified         → Open
 *
 * Cancelled lines are otherwise excluded from the active-line math so an SR
 * with 4 Fulfilled + 1 Cancelled lands at Fulfilled, not stuck Partially.
 */
export function deriveSourcingRequestStatus(
	input: DeriveSourcingRequestStatusInput,
): SourcingRequestStatusValue {
	const { currentStatus, lineStatuses } = input;

	// 1. Cancelled is sticky — manual override or every-line-cancelled both
	//    end up at Cancelled, and we don't unwind without an explicit reopen.
	if (currentStatus === SourcingRequestStatus.CANCELLED) {
		return SourcingRequestStatus.CANCELLED;
	}

	// 2. Draft is sticky until the user clicks Save & Open.
	if (currentStatus === SourcingRequestStatus.DRAFT) {
		return SourcingRequestStatus.DRAFT;
	}

	// 3. Lines exist and every one is cancelled → SR is cancelled.
	if (
		lineStatuses.length > 0 &&
		lineStatuses.every((s) => s === SourcingLineStatus.CANCELLED)
	) {
		return SourcingRequestStatus.CANCELLED;
	}

	const active = lineStatuses.filter((s) => s !== SourcingLineStatus.CANCELLED);

	// 4. No active lines → empty SR back-stop.
	if (active.length === 0) {
		return SourcingRequestStatus.OPEN;
	}

	const everyFulfilled = active.every(
		(s) => s === SourcingLineStatus.FULFILLED,
	);

	// 5. All active lines fulfilled.
	if (everyFulfilled) {
		return SourcingRequestStatus.FULFILLED;
	}

	const someFulfilled = active.some((s) => s === SourcingLineStatus.FULFILLED);

	// 6. Mix of fulfilled and not-fulfilled.
	if (someFulfilled) {
		return SourcingRequestStatus.PARTIALLY_FULFILLED;
	}

	// 7. Any active line at or past PO placement.
	if (active.some((s) => ACTIVE_PO_LINE_STATUSES.has(s))) {
		return SourcingRequestStatus.IN_PROGRESS;
	}

	// 8. Everything still in pre-PO states.
	return SourcingRequestStatus.OPEN;
}

/**
 * True if the given SR status represents a terminal / sticky state — i.e. the
 * derivation rule is bypassed in this state. UI uses this to gate things like
 * "edit lines" affordances on Cancelled SRs.
 */
export function isSourcingRequestStatusTerminal(
	status:
		| SourcingRequestStatusValue
		| SourcingRequestStatus
		| string
		| null
		| undefined,
): boolean {
	return status === SourcingRequestStatus.CANCELLED;
}

/**
 * True while the SR is in user-only Draft state (purchasing team should not
 * see it). UI uses this to filter list views.
 */
export function isSourcingRequestStatusDraft(
	status:
		| SourcingRequestStatusValue
		| SourcingRequestStatus
		| string
		| null
		| undefined,
): boolean {
	return status === SourcingRequestStatus.DRAFT;
}

/**
 * Result of a transition validation check. `allowed=false` carries a reason
 * suitable for surfacing to the user (e.g. as a 400-response body).
 */
export interface SourcingRequestStatusTransitionCheck {
	allowed: boolean;
	reason?: string;
}

/**
 * Validate whether a manual SR-status transition is permitted under the
 * locked status contract. Mirrors `deriveSourcingRequestStatus` — together
 * they describe what a user can change (this) and what the system derives
 * (deriveSourcingRequestStatus). The only manual transitions allowed are:
 *
 *   - `Draft → any non-Draft status` — the "Save & Open" flow leaves Draft.
 *     After leaving, derivation re-asserts the right status from line state.
 *   - `(any non-Cancelled) → Cancelled` — the override that pulls the SR
 *     out of every workflow.
 *   - `Cancelled → Open` — the reopen path (selective per-line restore
 *     happens via the dedicated reopen endpoint, which sets status=OPEN
 *     and lets `deriveSourcingRequestStatus` recompute from the
 *     restored line state).
 *
 * Everything else (Open ↔ InProgress ↔ PartiallyFulfilled ↔ Fulfilled,
 * Cancelled → non-Open, anything ↔ Draft) is derived or disallowed.
 */
export function checkSourcingRequestStatusTransition(
	currentStatus:
		| SourcingRequestStatusValue
		| SourcingRequestStatus
		| string
		| null
		| undefined,
	requestedStatus: SourcingRequestStatusValue | SourcingRequestStatus | string,
): SourcingRequestStatusTransitionCheck {
	if (currentStatus === requestedStatus) {
		return { allowed: true };
	}

	if (currentStatus === SourcingRequestStatus.CANCELLED) {
		// Reopen path: the only legal exit from Cancelled is straight to
		// Open. The reopen endpoint always lands here, and derivation
		// re-asserts the right status from the restored line state on
		// the next save. Anything else (→ Draft, → InProgress, etc.)
		// would skip the line-restore decision and is forbidden.
		if (requestedStatus === SourcingRequestStatus.OPEN) {
			return { allowed: true };
		}

		return {
			allowed: false,
			reason:
				'A cancelled sourcing request can only be reopened to "Open" — use the Reopen action to restore lines.',
		};
	}

	if (requestedStatus === SourcingRequestStatus.CANCELLED) {
		return { allowed: true };
	}

	if (requestedStatus === SourcingRequestStatus.DRAFT) {
		return {
			allowed: false,
			reason:
				'A sourcing request cannot be reverted to Draft once it has been opened.',
		};
	}

	if (currentStatus === SourcingRequestStatus.DRAFT) {
		return { allowed: true };
	}

	return {
		allowed: false,
		reason: `Sourcing request status "${requestedStatus}" cannot be set manually; it is derived from line statuses. Manual override is only allowed for "cancelled".`,
	};
}
